/**
 * Graphics Library panel — dynamic folder/template rendering from store
 */
import { libraryStore } from '../stores/libraryStore';
import { selectionStore } from '../stores/selectionStore';
import { templateRegistry } from '../engine/templateRegistry';
import { eventBus } from '../engine/eventBus';
import type { TemplateDefinition } from '@graphicsim/shared-types';

let gridEl: HTMLElement;
let navTitleEl: HTMLElement;
let backBtn: HTMLElement;

export function initLibrary(): void {
  gridEl = document.getElementById('library-grid')!;
  navTitleEl = document.querySelector('.lib-nav-title')!;
  backBtn = document.querySelector('.lib-back-btn')!;

  // Initialize store with folder data
  libraryStore.getState().setFolders(templateRegistry.getFolders());

  // Back button
  backBtn.addEventListener('click', () => {
    if (libraryStore.getState().currentFolderId) {
      libraryStore.getState().goBack();
    }
  });

  // Grid/List view toggle
  document.querySelectorAll<HTMLButtonElement>('.lib-view-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      const isGrid = btn.title === 'Grid View';
      libraryStore.getState().setViewMode(isGrid ? 'grid' : 'list');
    });
  });

  // Subscribe to store changes
  libraryStore.subscribe(render);

  // Initial render
  render();
}

function render(): void {
  const state = libraryStore.getState();

  // Update nav title — back button always visible (dimmed at root, like reference software)
  if (state.currentFolderId) {
    const folder = templateRegistry.getFolder(state.currentFolderId);
    navTitleEl.textContent = folder?.name ?? 'Unknown';
    backBtn.classList.remove('disabled');
  } else {
    navTitleEl.textContent = 'NEVCO Pro Video Content';
    backBtn.classList.add('disabled');
  }

  // Update view toggle buttons
  document.querySelectorAll<HTMLButtonElement>('.lib-view-btn').forEach((btn) => {
    const isGrid = btn.title === 'Grid View';
    btn.classList.toggle('active', (isGrid && state.viewMode === 'grid') || (!isGrid && state.viewMode === 'list'));
  });

  // Render content
  if (state.currentFolderId) {
    const templates = templateRegistry.getTemplatesForFolder(state.currentFolderId);
    renderTemplates(templates, state.viewMode);
  } else {
    renderFolders(state.viewMode);
  }
}

function renderFolders(viewMode: 'grid' | 'list'): void {
  const folders = libraryStore.getState().folders;
  gridEl.className = viewMode === 'grid' ? 'lib-grid' : 'lib-grid lib-list';

  gridEl.innerHTML = folders.map((folder) => `
    <div class="lib-folder" data-folder="${folder.id}">
      <div class="folder-thumb">
        <img class="icon-default" src="/assets/icons/folder-modern-lg.png" alt="">
        <img class="icon-hover" src="/assets/icons/folder-modern-lg_hover.png" alt="">
      </div>
      <span class="folder-label">${folder.name}</span>
    </div>
  `).join('');

  // Attach click handlers
  gridEl.querySelectorAll<HTMLDivElement>('.lib-folder').forEach((el) => {
    el.addEventListener('click', () => {
      const folderId = el.dataset.folder!;
      libraryStore.getState().enterFolder(folderId);
    });
  });
}

function renderTemplates(templates: TemplateDefinition[], viewMode: 'grid' | 'list'): void {
  gridEl.className = viewMode === 'grid' ? 'lib-grid' : 'lib-grid lib-list';

  const selectedId = selectionStore.getState().current;
  const selectedTemplateId = selectedId.type === 'template' ? selectedId.templateId : null;

  gridEl.innerHTML = templates.map((t) => {
    const isSelected = t.id === selectedTemplateId;
    const thumb = getTemplateThumbnail(t);
    return `
      <div class="lib-template${isSelected ? ' selected' : ''}" data-template-id="${t.id}" draggable="true">
        <div class="template-thumb">
          ${thumb}
        </div>
        <span class="template-label">${t.name}</span>
      </div>
    `;
  }).join('');

  // Attach click + drag handlers
  gridEl.querySelectorAll<HTMLDivElement>('.lib-template').forEach((el) => {
    const templateId = el.dataset.templateId!;

    el.addEventListener('click', () => {
      selectionStore.getState().select({ type: 'template', templateId });
      eventBus.emit('library:templateSelected', { templateId });
      // Re-render to update selection highlight
      const tpls = templateRegistry.getTemplatesForFolder(libraryStore.getState().currentFolderId!);
      renderTemplates(tpls, libraryStore.getState().viewMode);
    });

    el.addEventListener('dragstart', (e) => {
      e.dataTransfer!.setData('application/x-template-id', templateId);
      e.dataTransfer!.effectAllowed = 'copy';
      el.classList.add('dragging');
    });

    el.addEventListener('dragend', () => {
      el.classList.remove('dragging');
    });
  });
}

/**
 * Generate a thumbnail for a template — uses actual SVG content or layer-based preview
 */
function getTemplateThumbnail(template: TemplateDefinition): string {
  if (template.svgContent) {
    // Inject the SVG directly — it will scale to fit via CSS
    return `<div class="template-thumb-svg">${template.svgContent}</div>`;
  }

  // Layer-based templates: render a simplified mini preview
  if (template.layers.length > 0) {
    const w = template.width || 1280;
    const h = template.height || 720;
    const layerHtml = template.layers
      .filter((l) => l.visible !== false)
      .map((l) => {
        const xPct = (l.x / w * 100).toFixed(2);
        const yPct = (l.y / h * 100).toFixed(2);
        const wPct = (l.width / w * 100).toFixed(2);
        const hPct = (l.height / h * 100).toFixed(2);
        let style = `position:absolute;left:${xPct}%;top:${yPct}%;width:${wPct}%;height:${hPct}%;`;
        if (l.fill) style += `background:${l.fill};`;
        if (l.borderRadius) style += `border-radius:${l.borderRadius}px;`;
        if (l.opacity !== undefined) style += `opacity:${l.opacity};`;
        if (l.type === 'text' && l.fontSize) {
          style += `font-size:${Math.max(3, l.fontSize * 0.04)}px;color:${l.textColor || '#fff'};`;
          style += `overflow:hidden;white-space:nowrap;`;
        }
        const content = l.type === 'text' ? (l.content || '').replace(/\{.*?\}/g, 'Aa') : '';
        return `<div style="${style}">${content}</div>`;
      })
      .join('');
    return `<div class="template-thumb-layers">${layerHtml}</div>`;
  }

  // Fallback icon
  return `<span class="template-icon">&#x25A0;</span>`;
}
